import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Loader2, Mail, Phone, Building2, RefreshCw } from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export const RequestsAdmin = () => {
  const token = sessionStorage.getItem("admin_token");
  const [authed, setAuthed] = useState(Boolean(token));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [subs, setSubs] = useState(null);

  const loadRows = async (tok) => {
    try {
      const [{ data: reqs }, { data: newsletter }] = await Promise.all([
        axios.get(`${API}/admin/requests`, { headers: { Authorization: `Bearer ${tok}` } }),
        axios.get(`${API}/admin/newsletter`, { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      setRows(reqs);
      setSubs(newsletter);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setAuthed(false);
      } else {
        toast.error("Impossibile caricare i dati.");
      }
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem("admin_token", data.token);
      setAuthed(true);
      await loadRows(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accesso non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setAuthed(false);
    setRows(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F3EFE6] flex items-center justify-center px-6 py-24">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-[#FBF9F5] border border-[#E6DFD3] p-10 space-y-8">
          <div className="flex items-center gap-3">
            <LogoMark className="w-9 h-9" />
            <div>
              <h1 className="font-serif text-3xl text-[#1C1917]">Area riservata</h1>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#57534E]">Richieste ricevute</p>
            </div>
          </div>
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#57534E]">Email</span>
            <input data-testid="admin-input-email" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#57534E]">Password</span>
            <input data-testid="admin-input-password" type="password" required className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button disabled={loading} data-testid="admin-login-button" className="btn-pill bg-[#29221D] text-[#FBF9F5] disabled:opacity-60 w-full justify-center">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Accedi
          </button>
          <a href="/" className="block text-center text-sm text-[#57534E] link-underline">Torna al sito</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3EFE6] px-6 sm:px-12 lg:px-20 py-16">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <LogoMark className="w-9 h-9" />
            <h1 data-testid="admin-title" className="font-serif text-3xl lg:text-4xl text-[#1C1917]">Richieste ricevute</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => loadRows(sessionStorage.getItem("admin_token"))} data-testid="admin-refresh-button" className="btn-pill border border-[#29221D] text-[#29221D]">
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="btn-pill bg-[#29221D] text-[#FBF9F5]">
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>

        {rows === null ? (
          <p data-testid="admin-loading" className="mt-16 text-[#57534E]">Caricamento…</p>
        ) : rows.length === 0 ? (
          <p data-testid="admin-empty" className="mt-16 text-[#57534E]">Nessuna richiesta ancora: appena un cliente compila il modulo la trovi qui.</p>
        ) : (
          <div className="mt-12 space-y-4">
            {rows.map((r) => (
              <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-[#FBF9F5] border border-[#E6DFD3] p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif text-2xl text-[#1C1917]">{r.name}{r.company ? <span className="text-[#57534E]"> · {r.company}</span> : null}</h2>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#57534E]">{fmt(r.created_at)}{r.business_type ? ` · ${r.business_type}` : ""}{r.marketing ? " · vuole ricevere offerte" : ""}</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 text-sm">
                    <a href={`mailto:${r.email}`} data-testid={`admin-request-mail-${r.id}`} className="btn-pill border border-[#29221D] text-[#29221D] !py-2 !px-4"><Mail className="w-4 h-4" /> {r.email}</a>
                    {r.phone ? <a href={`tel:${r.phone}`} data-testid={`admin-request-call-${r.id}`} className="btn-pill bg-[#29221D] text-[#FBF9F5] !py-2 !px-4"><Phone className="w-4 h-4" /> {r.phone}</a> : null}
                  </div>
                </div>
                <p className="mt-5 text-[#57534E] leading-relaxed">{r.message}</p>
              </div>
            ))}
          </div>
        )}

        {subs !== null && subs.length > 0 && (
          <div data-testid="admin-newsletter" className="mt-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#9A4930]">Iscritti newsletter ({subs.length})</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {subs.map((s) => (
                <div key={s.id} data-testid={`admin-subscriber-${s.id}`} className="bg-[#FBF9F5] border border-[#E6DFD3] px-5 py-4 flex items-center justify-between gap-4">
                  <a href={`mailto:${s.email}`} className="text-sm text-[#1C1917] hover:text-[#9A4930] transition-colors break-all">{s.email}</a>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#57534E] shrink-0">{fmt(s.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
